import { fmtTime } from '../game/format';
import { ApiError, DRIVER_COLORS, DailyInfo, LadderChallenge, LadderInfo, Profile, RunSummary, api, loadProfile } from '../net/api';
import { trackMapSvg } from '../render/trackArt';
import { msToUkMidnight, ukDate } from '../shared/clock';
import type { Streak } from '../shared/streak';
import { CONDITIONS, DailySpec, TWISTS, dailySpec, trackById } from '../sim/daily';
import { teamById } from '../sim/teams';
import { CALENDAR } from '../sim/tracks/index';
import { NEWS } from './news';

/**
 * The website around the game (design.md B14.2): Daily, Records, driver
 * cards, Pit Board, About and Privacy, as panels over the running game.
 * Routes are real paths (/daily, /records, /driver/abc...) so links share.
 */

export interface SiteHost {
  go(path: string): void;
  startDaily(spec: DailySpec, ranked: boolean): void;
  /** Drive for a Seat Ladder challenge (set the lap, or defend). */
  startChallenge(c: LadderChallenge): void;
  blip(freq: number, secs: number): void;
  /** Your best Time Trial lap on a track (any car), from this browser. */
  localBest(trackId: string): { ticks: number; team: string } | null;
}

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

export const ROUTES = ['/daily', '/records', '/ladder', '/pitboard', '/about', '/privacy', '/driver'];

export function isSitePath(path: string): boolean {
  return ROUTES.some((r) => path === r || path.startsWith(`${r}/`));
}

/** The car for each Seat Ladder tier (1 Marlin … 6 Ferro). */
function tierTeamId(tier: number): string {
  return ['marlin', 'wolfe', 'bramley', 'lotos', 'bennet', 'ferro'][Math.max(1, Math.min(6, tier)) - 1];
}

function hex(n: number): string {
  return n.toString(16).padStart(6, '0');
}

function untilMidnight(): string {
  const m = Math.ceil(msToUkMidnight() / 60000);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
}

function chip(teamId: string): string {
  const t = teamById(teamId);
  return `<span class="chip" style="background:#${hex(t.primary)}"></span>`;
}

function initialsTag(initials: string, color: number, id?: string): string {
  const inner = `<span class="ini" style="color:${DRIVER_COLORS[color] ?? '#fff'}">${esc(initials)}</span>`;
  return id ? `<a href="/driver/${esc(id)}" data-link>${inner}</a>` : inner;
}

/** One line describing a Daily: track · car · conditions · twist. */
export function specLine(spec: DailySpec): string {
  const tr = trackById(spec.trackId).def;
  return `${tr.name} · ${teamById(spec.teamId).name} · ${CONDITIONS[spec.conditions].label} · ${TWISTS[spec.twist].label}`;
}

/**
 * The spoiler-free share card (design.md B14.3). Sector squares compare
 * each of your sectors with the leader's: green = as fast or faster,
 * yellow = within 3%, red = slower.
 */
export function shareText(spec: DailySpec, run: RunSummary, leaderSplits: number[], streak: Streak | null): string {
  const tr = trackById(spec.trackId).def;
  const lines = [`STEAL MY SEAT DAILY #${spec.number} \u{1F3C1}`, `${tr.name} · ${CONDITIONS[spec.conditions].label.toLowerCase()} · ${TWISTS[spec.twist].label.toLowerCase()}`];
  if (run.status === 'ok' && run.ticks !== null) {
    lines.push(`${fmtTime(run.ticks)}  P${run.rank ?? '?'} / ${run.total}`);
    const mine = sectors(run.splits, run.ticks);
    // leaderSplits = the leader's checkpoint splits followed by their lap time.
    const lead = leaderSplits.length === run.splits.length + 1 ? sectors(leaderSplits, null) : [];
    if (lead.length) {
      lines.push(
        mine
          .map((s, i) => {
            const l = lead[i];
            if (l === undefined || l <= 0) return '⬜';
            const r = s / l;
            return r <= 1.0005 ? '\u{1F7E9}' : r <= 1.03 ? '\u{1F7E8}' : '\u{1F7E5}';
          })
          .join(''),
      );
    }
  } else {
    lines.push(`DNF \u{1F4A5}  (${run.total} raced)`);
  }
  if (streak && streak.current > 1) lines.push(`\u{1F525} ${streak.current}-day streak`);
  lines.push('www.stealmyseat.co.uk/daily');
  return lines.join('\n');
}

/** Sector lengths from checkpoint splits (the last sector ends at the line). */
function sectors(splits: number[], total: number | null): number[] {
  const out: number[] = [];
  let prev = 0;
  for (const s of splits) {
    out.push(s - prev);
    prev = s;
  }
  if (total !== null) out.push(total - prev);
  return out;
}

export async function share(text: string): Promise<'shared' | 'copied' | 'failed'> {
  try {
    if (navigator.share && matchMedia('(pointer: coarse)').matches) {
      await navigator.share({ text });
      return 'shared';
    }
  } catch {
    /* cancelled: fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    return 'failed';
  }
}

export class Site {
  private host: SiteHost;
  private open = false;
  private token = 0;

  constructor(host: SiteHost) {
    this.host = host;
    // In-app links: <a href="/daily" data-link> anywhere in the document.
    document.addEventListener('click', (e) => {
      const a = (e.target as HTMLElement).closest?.('a[data-link]') as HTMLAnchorElement | null;
      if (!a || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      this.host.go(a.getAttribute('href') ?? '/');
    });
  }

  get visible(): boolean {
    return this.open;
  }

  hide(): void {
    this.open = false;
    $('site').hidden = true;
  }

  /** Render the page for `path`. */
  show(path: string): void {
    this.open = true;
    $('site').hidden = false;
    $('site').scrollTop = 0;
    const t = ++this.token;
    const body = $('site-body');
    document.querySelectorAll('#site .site-nav a').forEach((a) => a.classList.toggle('on', path.startsWith(a.getAttribute('href') ?? '#')));
    const [, page, arg] = path.split('/');
    const done = (html: string) => {
      if (t === this.token) body.innerHTML = html;
    };
    body.innerHTML = '<p class="muted center">Loading&hellip;</p>';
    switch (page) {
      case 'daily':
        void this.daily(t);
        break;
      case 'records':
        void this.records(t);
        break;
      case 'driver':
        void this.driver(t, arg ?? '');
        break;
      case 'ladder':
        void this.ladder(t);
        break;
      case 'pitboard':
        done(this.pitboard());
        break;
      case 'about':
        done(this.about());
        break;
      case 'privacy':
        done(this.privacy());
        break;
      default:
        done('<p>Page not found.</p>');
    }
  }

  // ================================================================ daily

  private async daily(t: number): Promise<void> {
    const body = $('site-body');
    let info: DailyInfo | null = null;
    let offline = false;
    try {
      info = await api.daily();
    } catch {
      offline = true;
    }
    if (t !== this.token) return;
    const spec = info?.spec ?? dailySpec(ukDate());
    const track = trackById(spec.trackId);
    const team = teamById(spec.teamId);
    const cond = CONDITIONS[spec.conditions];
    const twist = TWISTS[spec.twist];
    const profile = loadProfile();
    const me = info?.me ?? null;

    let action = '';
    if (offline) {
      action = `<div class="notice bad">Can't reach the server, so no ranked runs or leaderboard right now. Practice works offline.</div>
        <div class="row-buttons"><button class="primary" data-act="practice">PRACTICE</button></div>`;
    } else if (me) {
      action = `<div class="daily-result">${this.resultHtml(spec, me, info!)}</div>
        <div class="row-buttons"><button class="primary" data-act="share">SHARE RESULT</button><button class="secondary" data-act="practice">PRACTICE</button></div>
        <p class="muted center" id="share-msg"></p>`;
    } else {
      action = `<div class="row-buttons"><button class="secondary" data-act="practice">PRACTICE<small>unlimited</small></button>
        <button class="primary" data-act="ranked">RANKED RUN<small>one shot</small></button></div>
        ${profile ? '' : '<p class="muted center">You\'ll pick your arcade initials before your ranked run.</p>'}`;
    }
    const streak = info?.streak;
    const streakLine =
      streak && streak.current > 0
        ? `<div class="streak">\u{1F525} ${streak.current}-day streak${streak.today ? '' : ' &middot; race today to keep it'}${streak.pitStops ? ` &middot; ${streak.pitStops} pit stop${streak.pitStops > 1 ? 's' : ''} used` : ''}</div>`
        : '';

    const board = info
      ? info.top.length
        ? `<table class="board"><tr><th>POS</th><th>DRIVER</th><th class="num">TIME</th><th class="num">GAP</th></tr>${info.top
            .map(
              (r) =>
                `<tr class="${profile && r.id === profile.id ? 'me' : ''}"><td>${r.rank}</td><td>${initialsTag(r.initials, r.color, r.id)}</td><td class="num">${fmtTime(r.ticks)}</td><td class="num">${
                  r.rank === 1 ? '' : `+${((r.ticks - info!.top[0].ticks) / 120).toFixed(2)}`
                }</td></tr>`,
            )
            .join('')}</table><p class="muted center">${info.entries} driver${info.entries === 1 ? '' : 's'} raced today</p>`
        : '<p class="muted center">Nobody has set a time yet. Be the first on the board.</p>'
      : '';
    const y = info?.yesterday;
    const yHtml =
      y && y.top.length
        ? `<h3>YESTERDAY &middot; #${y.spec.number}</h3><p class="muted">${esc(specLine(y.spec))} &middot; ${y.entries} raced</p>
          <ol class="podium">${y.top.map((r) => `<li>${initialsTag(r.initials, r.color, r.id)} <span>${fmtTime(r.ticks)}</span></li>`).join('')}</ol>`
        : '';

    body.innerHTML = `
      <div class="sheet-kicker">DAILY GRAND PRIX #${spec.number} &middot; ${spec.date}</div>
      <h2>${esc(track.def.name)}</h2>
      <p class="tag">New Daily in ${untilMidnight()} (midnight UK)</p>
      <div class="daily-spec">
        <div class="d-map">${trackMapSvg(track, 120)}</div>
        <div class="d-facts">
          <div><span class="lbl">CAR</span> ${chip(team.id)}<b>${team.name}</b> <span class="muted">${team.zeroTo100.toFixed(1)}s &middot; ${team.topSpeedMph} mph</span></div>
          <div><span class="lbl">TRACK</span> <b>${cond.label}</b> <span class="muted">${esc(cond.desc)}</span></div>
          <div><span class="lbl">TWIST</span> <b>${twist.label}</b> <span class="muted">${esc(twist.desc)}</span></div>
          <div class="muted">${esc(track.def.country)} &middot; ${esc(track.def.subtitle)} &middot; standing-start lap</div>
        </div>
      </div>
      ${streakLine}
      ${action}
      ${board ? `<h3>TODAY'S BOARD</h3>${board}` : ''}
      ${yHtml}`;

    body.querySelectorAll<HTMLButtonElement>('[data-act]').forEach((b) => {
      b.onclick = () => {
        const act = b.dataset.act;
        if (act === 'practice') this.host.startDaily(spec, false);
        if (act === 'ranked') void this.confirmRanked(spec);
        if (act === 'share' && me && info) {
          void share(shareText(spec, me, info.leaderSplits, info.streak)).then((r) => {
            $('share-msg').textContent = r === 'copied' ? 'Copied! Paste it anywhere.' : r === 'shared' ? '' : 'Could not copy on this browser.';
          });
        }
      };
    });
  }

  resultHtml(spec: DailySpec, run: RunSummary, info: { entries: number }): string {
    void spec;
    if (run.status === 'ok' && run.ticks !== null) {
      return `<div class="big-time">${fmtTime(run.ticks)}</div><div class="big-pos">P${run.rank} <small>/ ${Math.max(run.total, info.entries)}</small></div>`;
    }
    if (run.status === 'rejected') return '<div class="big-pos">NOT COUNTED</div><p class="muted">That run didn\'t replay the same on the server.</p>';
    return '<div class="big-pos">DNF</div><p class="muted">No time today. Tomorrow\'s another Daily.</p>';
  }

  private async confirmRanked(spec: DailySpec): Promise<void> {
    const profile = await this.ensureProfile();
    if (!profile) return;
    const body = $('site-body');
    const track = trackById(spec.trackId).def;
    body.innerHTML = `
      <div class="sheet-kicker">DAILY #${spec.number} &middot; RANKED RUN</div>
      <h2>ONE SHOT</h2>
      <ol class="rules">
        <li>One standing-start lap of ${esc(track.name)} in the ${teamById(spec.teamId).name}.</li>
        <li>Crash or miss a checkpoint and it's a <b>DNF</b>.</li>
        <li>It counts from the moment you press GO: quitting or reloading is a DNF.</li>
      </ol>
      <div class="row-buttons"><button class="secondary" data-act="back">NOT YET</button><button class="primary" data-act="go">GO</button></div>
      <p class="muted center" id="ranked-msg"></p>`;
    (body.querySelector('[data-act="back"]') as HTMLButtonElement).onclick = () => this.show('/daily');
    (body.querySelector('[data-act="go"]') as HTMLButtonElement).onclick = async () => {
      const msg = $('ranked-msg');
      msg.textContent = 'Checking in…';
      try {
        await api.startDaily(spec.date);
        this.host.startDaily(spec, true);
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) this.show('/daily');
        else if (e instanceof ApiError && e.status === 400) msg.textContent = 'That Daily has just closed. Here comes a new one!';
        else msg.textContent = "Can't reach the server: ranked runs need a connection. Practice works offline.";
      }
    };
  }

  // ============================================================== profile

  /** Make sure this browser has a driver; asks for initials the first time. */
  ensureProfile(): Promise<Profile | null> {
    const p = loadProfile();
    if (p) return Promise.resolve(p);
    return new Promise((resolve) => {
      const body = $('site-body');
      body.innerHTML = `
        <div class="sheet-kicker">NEW DRIVER</div>
        <h2>YOUR INITIALS</h2>
        <p class="tag">Three letters for the leaderboards, arcade style. No sign-up, no email.</p>
        <input id="ini-input" class="ini-input" maxlength="3" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="AAA" />
        <div class="swatches">${DRIVER_COLORS.map((c, i) => `<button class="sw${i === 0 ? ' on' : ''}" data-c="${i}" style="background:${c}" aria-label="Colour ${i + 1}"></button>`).join('')}</div>
        <div class="row-buttons"><button class="secondary" data-act="cancel">CANCEL</button><button class="primary" data-act="ok">SAVE</button></div>
        <p class="muted center" id="ini-msg">Your driver lives in this browser.</p>
        <div class="claim-box"><div class="muted">ALREADY RACE ON ANOTHER DEVICE? Get a code from your driver card there.</div>
          <div class="net-join"><input id="claim-code" class="code-input" maxlength="6" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="CODE" />
          <button class="secondary" data-act="claim">I HAVE A CODE</button></div></div>`;
      const codeIn = $<HTMLInputElement>('claim-code');
      codeIn.oninput = () => (codeIn.value = codeIn.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6));
      codeIn.onkeydown = (e) => e.stopPropagation();
      (body.querySelector('[data-act="claim"]') as HTMLButtonElement).onclick = async () => {
        const msg = $('ini-msg');
        try {
          const prof = await api.claim(codeIn.value);
          this.host.blip(880, 0.1);
          msg.textContent = `Welcome back, ${prof.initials}!`;
          resolve(prof);
        } catch (e) {
          msg.textContent = e instanceof ApiError ? e.message : "Can't reach the server right now.";
        }
      };
      const input = $<HTMLInputElement>('ini-input');
      let color = 0;
      input.oninput = () => (input.value = input.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3));
      body.querySelectorAll<HTMLButtonElement>('.sw').forEach((b) => {
        b.onclick = () => {
          color = Number(b.dataset.c);
          body.querySelectorAll('.sw').forEach((x) => x.classList.toggle('on', x === b));
        };
      });
      (body.querySelector('[data-act="cancel"]') as HTMLButtonElement).onclick = () => {
        resolve(null);
        this.host.go('/daily');
      };
      const save = async () => {
        const msg = $('ini-msg');
        if (!/^[A-Z]{3}$/.test(input.value)) {
          msg.textContent = 'Three letters, please.';
          return;
        }
        try {
          const prof = await api.createProfile(input.value, color);
          this.host.blip(880, 0.1);
          resolve(prof);
        } catch (e) {
          msg.textContent = e instanceof ApiError ? (e.status === 400 ? 'Try different initials.' : e.message) : "Can't reach the server right now.";
        }
      };
      (body.querySelector('[data-act="ok"]') as HTMLButtonElement).onclick = () => void save();
      input.onkeydown = (e) => {
        e.stopPropagation();
        if (e.key === 'Enter') void save();
      };
      setTimeout(() => input.focus(), 50);
    });
  }

  // ============================================================== records

  private async records(t: number): Promise<void> {
    let rec: Awaited<ReturnType<typeof api.records>> | null = null;
    try {
      rec = await api.records();
    } catch {
      rec = null;
    }
    if (t !== this.token) return;
    const cards = CALENDAR.map((def) => {
      const track = trackById(def.id);
      const top = rec?.tracks.find((x) => x.id === def.id)?.top ?? [];
      const mine = this.host.localBest(def.id);
      const rows = top.length
        ? top
            .map(
              (r, i) =>
                `<li><span class="p">${i + 1}</span>${initialsTag(r.initials, r.color, r.id)} ${chip(r.team)}<a class="tm" href="/ghost/${esc(r.id)}/${esc(def.id)}/${esc(r.team)}" data-link title="Race this lap as a ghost">${fmtTime(r.ticks)} &#9654;</a></li>`,
            )
            .join('')
        : `<li class="muted">${rec ? 'No times yet' : 'Offline'}</li>`;
      return `<div class="rec-card">
        <div class="rec-map">${trackMapSvg(track, 70)}</div>
        <div class="rec-name">${esc(def.name)}</div>
        <ol class="rec-top">${rows}</ol>
        <div class="muted">1992 benchmark ${fmtTime(Math.round(def.defaultRecord * 120))}${mine ? ` &middot; you ${fmtTime(mine.ticks)}` : ''}</div>
      </div>`;
    }).join('');
    $('site-body').innerHTML = `
      <div class="sheet-kicker">LAP RECORDS</div>
      <h2>THE RECORDS BOARD</h2>
      <p class="tag">Best Time Trial laps, any car. Every lap is replayed on the server before it counts. Tap a time to race it as a ghost.</p>
      <div class="rec-grid">${cards}</div>`;
  }

  // =============================================================== driver

  private async driver(t: number, idArg: string): Promise<void> {
    const mine = loadProfile();
    const id = idArg === 'me' || !idArg ? mine?.id : idArg;
    if (!id) {
      const p = await this.ensureProfile();
      if (p) this.host.go(`/driver/${p.id}`);
      return;
    }
    try {
      const d = await api.driver(id);
      if (t !== this.token) return;
      const isMe = mine?.id === d.id;
      const laps = d.laps.length
        ? `<table class="board"><tr><th>TRACK</th><th>CAR</th><th class="num">BEST</th></tr>${d.laps
            .map(
              (l) =>
                `<tr><td>${esc(trackById(l.track).def.name)}</td><td>${chip(l.team)}${teamById(l.team).name}</td><td class="num">${fmtTime(l.ticks)}</td><td class="num"><a class="race-link" href="/ghost/${esc(d.id)}/${esc(l.track)}/${esc(l.team)}" data-link title="Race this lap as a ghost">${isMe ? 'GHOST' : 'BEAT IT'} &#9654;</a></td></tr>`,
            )
            .join('')}</table>${isMe ? '<p class="muted">Send a friend the BEAT IT link from your card: they race your lap as a ghost.</p>' : ''}`
        : '<p class="muted">No lap records yet. Set a Time Trial best while online and it lands here.</p>';
      const recent = d.recent.length
        ? `<table class="board"><tr><th>DAILY</th><th>DATE</th><th class="num">TIME</th><th class="num">POS</th></tr>${d.recent
            .map((r) => `<tr><td>#${r.number}</td><td>${r.date}</td><td class="num">${r.status === 'ok' ? fmtTime(r.ticks) : 'DNF'}</td><td class="num">${r.rank ? `${r.rank}/${r.total}` : '-'}</td></tr>`)
            .join('')}</table>`
        : '<p class="muted">No Dailies yet.</p>';
      $('site-body').innerHTML = `
        <div class="sheet-kicker">DRIVER CARD${isMe ? ' &middot; YOU' : ''}</div>
        <h2 class="driver-ini" style="color:${DRIVER_COLORS[d.color] ?? '#fff'}">${esc(d.initials)}</h2>
        <p class="tag">Racing since ${new Date(d.created).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
        <div class="stat-row">
          <div><b>\u{1F525} ${d.streak.current}</b><span>STREAK</span></div>
          <div><b>${d.streak.best}</b><span>BEST STREAK</span></div>
          <div><b>${d.dailies}</b><span>DAILIES</span></div>
          <div><b>${d.wins}</b><span>DAILY WINS</span></div>
          <div><b>${d.podiums}</b><span>PODIUMS</span></div>
        </div>
        <div class="seat-line">${chip(d.ladder.team)}SEAT: <b>${teamById(d.ladder.team).name}</b> (tier ${d.ladder.tier}) &middot; seats taken ${d.ladder.won} &middot; lost ${d.ladder.lost} &middot; defended ${d.ladder.defended} &middot; <a href="/ladder" data-link>LADDER</a></div>
        <h3>LAP RECORDS</h3>${laps}
        <h3>RECENT DAILIES</h3>${recent}
        ${
          isMe
            ? `<h3>YOUR OTHER DEVICES</h3><p class="muted">Use this driver on your phone or another computer too: get a code here, then type it in there (Driver page &rarr; I HAVE A CODE). It works for 10 minutes.</p>
               <div class="row-buttons"><button class="secondary" data-act="link">GET A CODE</button></div><div id="link-code" class="big-code center" hidden></div>
               <h3>YOUR DATA</h3><p class="muted">Your driver lives in this browser. Clearing site data loses it, unless it's linked to another device.</p>
               <div class="row-buttons"><button class="secondary" data-act="export">DOWNLOAD MY DATA</button><button class="secondary danger" data-act="delete">DELETE MY DRIVER</button></div>
               <p class="muted center" id="drv-msg"></p>`
            : ''
        }`;
      if (isMe) {
        ($('site-body').querySelector('[data-act="link"]') as HTMLButtonElement).onclick = async () => {
          try {
            const { code } = await api.linkCode();
            const el = $('link-code');
            el.hidden = false;
            el.textContent = code;
          } catch {
            $('drv-msg').textContent = "Can't reach the server right now.";
          }
        };
        ($('site-body').querySelector('[data-act="export"]') as HTMLButtonElement).onclick = async () => {
          const blob = await api.exportData();
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = 'stealmyseat-driver.json';
          a.click();
          setTimeout(() => URL.revokeObjectURL(a.href), 5000);
        };
        const del = $('site-body').querySelector('[data-act="delete"]') as HTMLButtonElement;
        del.onclick = async () => {
          if (del.dataset.armed !== '1') {
            del.dataset.armed = '1';
            del.textContent = 'TAP AGAIN TO DELETE';
            return;
          }
          try {
            await api.deleteProfile();
            this.host.go('/');
          } catch {
            $('drv-msg').textContent = "Can't reach the server right now.";
          }
        };
      }
    } catch (e) {
      if (t !== this.token) return;
      $('site-body').innerHTML = e instanceof ApiError && e.status === 404 ? '<h2>NO SUCH DRIVER</h2>' : '<p class="muted center">Offline: driver cards need a connection.</p>';
    }
  }

  // ============================================================== ladder

  private async ladder(t: number): Promise<void> {
    let info: LadderInfo;
    try {
      info = await api.ladder();
    } catch {
      if (t === this.token) $('site-body').innerHTML = '<p class="muted center">Offline: the Seat Ladder needs a connection.</p>';
      return;
    }
    if (t !== this.token) return;
    const me = info.me;
    const body = $('site-body');
    const hours = (ms: number | null) => (ms === null ? '' : `${Math.max(0, Math.ceil((ms - Date.now()) / 3600_000))} h`);
    const tn = (id: string) => teamById(id).name;
    const challengeCard = (c: LadderChallenge): string => {
      const track = esc(trackById(c.track).def.name);
      const vs = c.emptySeat ? `an empty ${tn(c.defender?.team ?? tierTeamId(c.toTier))} seat` : `${esc(c.defender?.initials ?? '???')}'s ${tn(c.defender?.team ?? tierTeamId(c.toTier))}`;
      if (c.state === 'setting' && c.mine === 'challenger') {
        if (c.emptySeat) {
          return `<div class="ch-card"><b>Empty seat: ${tn(tierTeamId(c.toTier))}</b><br>Beat <b>${fmtTime(c.target)}</b> at ${track} in your ${tn(c.challenger.team)}.
            ${c.challengerTicks ? `Your best: ${fmtTime(c.challengerTicks)}.` : ''} ${hours(c.expiresSetting)} left.
            <div class="row-buttons"><button class="primary" data-drive="${c.id}">DRIVE</button><button class="secondary" data-cancel="${c.id}">GIVE UP</button></div></div>`;
        }
        return `<div class="ch-card"><b>Your challenge for ${vs}</b><br>Set your lap at ${track} in your ${tn(c.challenger.team)}, then send it.
          ${c.challengerTicks ? `Your lap: <b>${fmtTime(c.challengerTicks)}</b>.` : 'No lap yet.'} ${hours(c.expiresSetting)} left to send.
          <div class="row-buttons"><button class="secondary" data-drive="${c.id}">${c.challengerTicks ? 'IMPROVE IT' : 'DRIVE'}</button>${
            c.challengerTicks ? `<button class="primary" data-send="${c.id}">SEND ${fmtTime(c.challengerTicks)}</button>` : ''
          }<button class="secondary" data-cancel="${c.id}">CANCEL</button></div></div>`;
      }
      if (c.state === 'open' && c.mine === 'defender') {
        return `<div class="ch-card alert"><b>&#9888; ${esc(c.challenger.initials ?? '')} wants your ${tn(c.defender!.team)}!</b><br>
          They did <b>${fmtTime(c.target)}</b> at ${track} in a ${tn(c.challenger.team)}. Beat it in your ${tn(c.defender!.team)} within <b>${hours(c.deadline)}</b> or the seats swap.
          ${c.defenderTicks ? `Your best so far: ${fmtTime(c.defenderTicks)}.` : ''}
          <div class="row-buttons"><button class="primary" data-drive="${c.id}">DEFEND</button></div></div>`;
      }
      if (c.state === 'open') return `<div class="ch-card">Waiting for <b>${esc(c.defender?.initials ?? '???')}</b> to beat your <b>${fmtTime(c.target)}</b> at ${track} in their ${tn(c.defender?.team ?? tierTeamId(c.toTier))}: ${hours(c.deadline)} left.</div>`;
      const who = c.mine === 'challenger' ? 'You' : esc(c.challenger.initials ?? '');
      if (c.state === 'won') return `<div class="ch-card done">${who} took ${c.mine === 'defender' ? 'your' : c.emptySeat ? 'an empty' : esc(c.defender?.initials ?? '') + "'s"} ${tn(tierTeamId(c.toTier))} seat.</div>`;
      if (c.state === 'defended') return `<div class="ch-card done">${c.mine === 'defender' ? 'You defended your' : `${esc(c.defender?.initials ?? '')} defended their`} ${tn(tierTeamId(c.toTier))}.</div>`;
      return `<div class="ch-card done muted">Challenge at ${track} cancelled.</div>`;
    };
    const myCards = me?.challenges.length ? me.challenges.map(challengeCard).join('') : '';
    const tiers = info.tiers
      .map((tr) => {
        const above = me && tr.tier === me.tier - 1 && !me.busy;
        const drivers = tr.drivers.length
          ? tr.drivers
              .map((d) => {
                const isMe = me?.id === d.id;
                const btn = above && !d.busy ? `<button class="mini" data-challenge="${d.id}">CHALLENGE</button>` : '';
                return `<span class="seat${isMe ? ' me' : ''}${d.inactive ? ' idle' : ''}">${initialsTag(d.initials, d.color, d.id)}${d.busy ? ' <span class="muted">&#9876;</span>' : ''}${btn}</span>`;
              })
              .join('')
          : '<span class="muted">nobody yet</span>';
        const more = tr.count > tr.drivers.length ? `<span class="muted">+${tr.count - tr.drivers.length} more</span>` : '';
        const seats = tr.cap === null ? '' : `<span class="muted">${tr.count}/${tr.cap} seats</span>`;
        const empty = above && tr.open ? `<button class="mini gold" data-empty="1">TAKE AN EMPTY SEAT</button>` : '';
        return `<div class="tier-row${me?.tier === tr.tier ? ' mine' : ''}">
          <div class="tier-head">${chip(tr.team)}<b>${tn(tr.team)}</b> <span class="muted">TIER ${tr.tier}</span> ${seats}</div>
          <div class="tier-seats">${drivers}${more}${empty}</div></div>`;
      })
      .join('');
    body.innerHTML = `
      <div class="sheet-kicker">THE SEAT LADDER</div>
      <h2>STEAL MY SEAT</h2>
      <p class="tag">Everyone starts in the Bennet. Challenge a driver one tier up: set a lap on the track the game picks, and they get 48 hours to beat it in their faster car. If they can't, you take their seat.</p>
      ${
        me
          ? `<div class="seat-line">${chip(me.team)}Your seat: <b>${tn(me.team)}</b> (tier ${me.tier})${me.tier > 1 && !me.busy ? ' &middot; challenge someone in the row above' : me.busy ? ' &middot; one challenge at a time' : ' &middot; the top seat: defend it!'}</div>${myCards}`
          : `<div class="row-buttons"><button class="primary" data-act="join">GET A SEAT</button></div>`
      }
      <div class="ladder">${tiers}</div>
      <p class="muted center" id="ladder-msg"></p>`;
    const msg = (s: string) => ($('ladder-msg').textContent = s);
    const all = new Map((me?.challenges ?? []).map((c) => [c.id, c]));
    const act = async (fn: () => Promise<unknown>) => {
      try {
        await fn();
        this.show('/ladder');
      } catch (e) {
        msg(e instanceof ApiError ? e.message : "Can't reach the server right now.");
      }
    };
    body.querySelector<HTMLButtonElement>('[data-act="join"]')?.addEventListener('click', async () => {
      if (await this.ensureProfile()) this.host.go('/ladder');
    });
    body.querySelectorAll<HTMLButtonElement>('[data-challenge]').forEach((b) => (b.onclick = () => act(() => api.challenge(b.dataset.challenge!))));
    body.querySelectorAll<HTMLButtonElement>('[data-empty]').forEach((b) => (b.onclick = () => act(() => api.challenge(null))));
    body.querySelectorAll<HTMLButtonElement>('[data-send]').forEach((b) => (b.onclick = () => act(() => api.sendChallenge(Number(b.dataset.send)))));
    body.querySelectorAll<HTMLButtonElement>('[data-cancel]').forEach((b) => (b.onclick = () => act(() => api.cancelChallenge(Number(b.dataset.cancel)))));
    body.querySelectorAll<HTMLButtonElement>('[data-drive]').forEach((b) => {
      b.onclick = async () => {
        // Fetch the full challenge (the defender needs the challenger's ghost).
        try {
          const c = await api.getChallenge(Number(b.dataset.drive));
          this.host.startChallenge(c.mine ? c : (all.get(c.id) ?? c));
        } catch {
          msg("Can't reach the server right now.");
        }
      };
    });
  }

  // ========================================================= static pages

  private pitboard(): string {
    return `<div class="sheet-kicker">NEWS &amp; PATCH NOTES</div><h2>PIT BOARD</h2>
      ${NEWS.map((n) => `<article class="news"><div class="muted">${n.date}</div><h3>${esc(n.title)}</h3><p>${esc(n.body)}</p></article>`).join('')}`;
  }

  private about(): string {
    return `<div class="sheet-kicker">A TRIBUTE</div><h2>ABOUT</h2>
      <p class="prose">Steal My Seat is a free, independent fan remake of <em>Slicks</em>, the top-down Grand Prix game
      Codemasters published for the Commodore 64 in 1992. It was made by Digital Design: Ashley Routledge and
      David Saunders, with music by Allister Brimble.</p>
      <p class="prose">The heart of the original was its challenge system: race a rival for their car, and lose yours if
      someone slower beats you. That's where the name comes from. The six circuits are traced from the original's maps
      (Phoenix, Monaco, Monza, Le Castellet, Silverstone and Rio), renamed, with team and driver names that only nod to 1992.</p>
      <p class="prose">This project has no connection with Codemasters, EA or Formula One. Built in a browser with TypeScript
      and PixiJS, and hosted at home.</p>
      <p class="prose muted">Every ranked lap is re-simulated on the server from its controller inputs, so leaderboards stay honest.</p>`;
  }

  private privacy(): string {
    return `<div class="sheet-kicker">PLAIN ENGLISH</div><h2>PRIVACY</h2>
      <ul class="prose-list">
        <li><b>No cookies, no trackers, no ads.</b> So there's no cookie banner.</li>
        <li><b>On your device:</b> settings, your season, lap times and ghosts are kept in your browser's storage. They never leave it unless you race online (below).</li>
        <li><b>Your driver:</b> when you pick initials for the Daily, we store a random driver ID, your 3 initials and colour, and a secret key (hashed) that proves the driver is yours. No name, email or password.</li>
        <li><b>Your races:</b> ranked Daily runs and Time Trial bests are stored with their controller inputs so the server can replay them and check the time. Times and initials are shown on public leaderboards and your driver card.</li>
        <li><b>IP addresses</b> are used for a few minutes in memory to stop abuse (rate limits) and are not written to disk.</li>
        <li><b>Your rights:</b> download everything we hold about you, or delete your driver and all its races, from your driver card. Deleting is immediate; backups roll off within 14 days.</li>
        <li><b>Where:</b> the site runs on a home server in the UK.</li>
      </ul>`;
  }

  // ======================================================== title widget

  /** The Daily card on the home screen: today's combination + your status. */
  async renderTitleCard(): Promise<void> {
    const spec = dailySpec(ukDate());
    const el = $('daily-card');
    const track = trackById(spec.trackId);
    el.querySelector('.dc-map')!.innerHTML = trackMapSvg(track, 64);
    el.querySelector('.dc-line')!.innerHTML = `<b>#${spec.number}</b> ${esc(track.def.name)}<br>${teamById(spec.teamId).name} &middot; ${CONDITIONS[spec.conditions].label} &middot; ${TWISTS[spec.twist].label}`;
    const status = el.querySelector('.dc-status') as HTMLElement;
    status.textContent = `New Daily in ${untilMidnight()}`;
    if (!loadProfile()) return;
    try {
      const me = await api.me();
      const s = me.streak;
      const run = me.today;
      const streak = s.current > 0 ? `\u{1F525} ${s.current}` : '';
      if (run) status.textContent = `${run.status === 'ok' ? `P${run.rank}/${run.total} ${fmtTime(run.ticks)}` : 'DNF today'} ${streak}`;
      else status.textContent = s.current > 0 ? `${streak} race today to keep your streak!` : `New Daily in ${untilMidnight()}`;
      $('btn-me').textContent = me.initials;
      $('btn-me').style.color = DRIVER_COLORS[me.color] ?? '';
      const la = $('ladder-alert');
      const a = me.ladderAlert;
      if (a && a.kind === 'defend') {
        const h = Math.max(0, Math.ceil((a.deadline - Date.now()) / 3600_000));
        la.innerHTML = `&#9888; <b>${esc(a.by)}</b> wants your ${teamById(me.ladder.team).name}! Beat ${fmtTime(a.target)} at ${esc(trackById(a.track).def.name)} within ${h} h &#9654;`;
        la.hidden = false;
      } else if (a && a.kind === 'setting') {
        la.innerHTML = `Your Seat Ladder challenge at ${esc(trackById(a.track).def.name)} is waiting for your lap &#9654;`;
        la.hidden = false;
      } else {
        la.innerHTML = `${chip(me.ladder.team)}Your seat: <b>${teamById(me.ladder.team).name}</b> (tier ${me.ladder.tier}). Steal a better one on the Seat Ladder &#9654;`;
        la.hidden = false;
      }
    } catch {
      /* offline: keep the basic card */
    }
  }
}
